import { makeEventNamespace } from '@os/dom/events'
import {
  findElements,
  findOneElement,
  findOneElementOfType
} from '@os/dom/traversal'
import { initMainBus } from '@os/messaging'

/**
 * `<product-images>` — the product gallery: a scroll-snap main carousel synced
 * with a thumbnail strip, pagination bullets, prev/next navigation, per-slide
 * video controls, a featured-video slide and a fullscreen lightbox.
 *
 * Hand-authored module, included next to its markup with
 * `<script src="{{ 'product-images.js' | asset_url }}" type="module">`. Shared
 * behavior comes from the `@os/*` modules resolved by the theme import map. See
 * `docs/architecture.md`.
 *
 * Markup contract:
 * - `.js-product-gallery-images[data-product-id]` wraps the gallery.
 * - `[data-gallery-track]` / `[data-thumbs-track]` are the two scroll tracks;
 *   their direct children carry `.js-product-gallery-item`.
 * - `.js-product-gallery-image` marks main image slides (used for variant
 *   hydration).
 * - `.js-product-gallery-prev` / `.js-product-gallery-next` are the nav buttons.
 * - `.js-product-gallery-pagination` receives the bullets.
 * - `.js-lightbox-trigger` opens the lightbox; `.js-product-gallery-lightbox`
 *   is the `<dialog>` it opens, `.js-product-gallery-lightbox-image` its image
 *   and `.js-product-gallery-lightbox-close` its close button.
 *
 * Variant changes arrive over the main message bus
 * (`notification:selected-variant`) and re-filter the gallery.
 *
 * The former PhotoSwipe 4 lightbox is replaced with a native `<dialog>`: the
 * viewer needs Esc-to-close, focus handling and top-layer stacking, all of
 * which the platform now provides. PhotoSwipe was the only npm runtime
 * dependency here.
 *
 * The pure helpers (`normalizeImageUrl`, `withVideoAutoplay`, `getGalleryItems`,
 * `createGalleryIndex`) are inlined here (the element is their only consumer)
 * and exported so they can be unit-tested through the bundle.
 */

const GALLERY_ITEM_SELECTOR = '.js-product-gallery-item'
const IMAGE_PLACEHOLDER_CLASS = 'product-gallery__image-placeholder'
const THUMB_PLACEHOLDER_CLASS = 'product-gallery__thumb-placeholder'

/**
 * @returns {boolean}
 */
const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * @param {unknown} value
 * @returns {value is NonNullable<unknown>}
 */
const isPresent = (value) => value !== undefined && value !== null

/**
 * Normalize image URLs so we can reliably compare Shopify CDN URLs with
 * possible query-string/size differences.
 *
 * @param {unknown} value
 * @returns {string | null}
 */
export const normalizeImageUrl = (value) => {
  if (typeof value !== 'string' || value.length === 0) {
    return null
  }

  // Keep absolute URLs, and also support protocol-relative values.
  const normalizedValue = value.startsWith('//') ? `https:${value}` : value

  try {
    const parsed = new URL(normalizedValue, window.location.origin)
    return `${parsed.origin}${parsed.pathname}`
  } catch {
    return normalizedValue.split('?')[0] || null
  }
}

/**
 * Direct children of `wrapper` carrying the gallery-item class.
 *
 * @param {Element} wrapper
 * @returns {HTMLElement[]}
 */
export const getGalleryItems = (wrapper) => {
  return Array.from(wrapper.children).filter((el) => {
    return (
      el instanceof HTMLElement &&
      el.classList.contains(GALLERY_ITEM_SELECTOR.slice(1))
    )
  })
}

/**
 * @param {HTMLElement} mainWrapper
 * @param {HTMLElement} thumbWrapper
 * @returns {{ mainItems: HTMLElement[], thumbItems: HTMLElement[], mainImageItems: HTMLElement[], mainImageMediaIndexes: number[] }}
 */
export const createGalleryIndex = (mainWrapper, thumbWrapper) => {
  const mainItems = getGalleryItems(mainWrapper)
  const thumbItems = getGalleryItems(thumbWrapper)
  const mainImageItems = findElements(mainWrapper, '.js-product-gallery-image')
  const mainImageMediaIndexes = mainImageItems
    .map((slide) => mainItems.indexOf(slide))
    .filter((index) => index >= 0)

  return {
    mainItems,
    thumbItems,
    mainImageItems,
    mainImageMediaIndexes
  }
}

const VARIANT_PRELOAD_ATTRIBUTE = 'data-variant-preload'
const MAX_VARIANT_PRELOADS = 2
const imageObjectUrlCache = new Map()
const imageWarmPromiseCache = new Map()

/**
 * Add autoplay/muted/playsinline to an embed URL, plus the YouTube JS API flag.
 *
 * @param {string} src
 * @returns {string}
 */
export const withVideoAutoplay = (src) => {
  if (!src) {
    return src
  }

  try {
    const url = new URL(src, window.location.origin)
    url.searchParams.set('autoplay', '1')
    url.searchParams.set('muted', '1')
    url.searchParams.set('playsinline', '1')

    if (
      url.hostname.includes('youtube.com') ||
      url.hostname.includes('youtu.be')
    ) {
      url.searchParams.set('enablejsapi', '1')
    }

    return url.toString()
  } catch {
    return src
  }
}

/**
 * @param {HTMLElement} featuredVideoMedia
 * @param {'playing' | 'paused'} state
 */
const setFeaturedVideoState = (featuredVideoMedia, state) => {
  featuredVideoMedia.setAttribute('data-state', state)
}

/**
 * @param {HTMLElement} featuredVideoMedia
 */
const dismissFeaturedVideoPoster = (featuredVideoMedia) => {
  featuredVideoMedia.setAttribute('data-poster-dismissed', 'true')
}

/**
 * @param {HTMLElement} slide
 */
const showSlide = (slide) => {
  slide.classList.remove('u-hide')
}

/**
 * @param {HTMLElement} slide
 */
const hideSlide = (slide) => {
  slide.classList.add('u-hide')
}

/**
 * @param {string} imageUrl
 * @returns {string}
 */
const getRenderableImageUrl = (imageUrl) => {
  return imageObjectUrlCache.get(imageUrl) ?? imageUrl
}

/**
 * Fetch an image once and hand back an object URL, so a variant swap can reuse
 * the decoded bytes instead of re-downloading.
 *
 * @param {string} imageUrl
 * @returns {Promise<string>}
 */
const warmImageCache = (imageUrl) => {
  const cachedUrl = imageObjectUrlCache.get(imageUrl)
  if (cachedUrl) {
    return Promise.resolve(cachedUrl)
  }

  const pendingWarm = imageWarmPromiseCache.get(imageUrl)
  if (pendingWarm) {
    return pendingWarm
  }

  const warmPromise = fetch(imageUrl)
    .then(async (response) => {
      if (!response.ok) {
        return imageUrl
      }

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      imageObjectUrlCache.set(imageUrl, objectUrl)
      return objectUrl
    })
    .catch(() => imageUrl)
    .finally(() => {
      imageWarmPromiseCache.delete(imageUrl)
    })

  imageWarmPromiseCache.set(imageUrl, warmPromise)
  return warmPromise
}

/**
 * @param {HTMLElement} placeholder
 * @param {string} imageUrl
 * @param {string} label
 */
const setImagePlaceholder = (placeholder, imageUrl, label) => {
  placeholder.classList.remove('lazyloaded')
  placeholder.classList.remove('lazyload')
  placeholder.classList.add('lazyloaded')
  placeholder.setAttribute('role', 'img')
  placeholder.setAttribute('aria-label', label)
  placeholder.setAttribute('data-sizes', 'auto')
  placeholder.setAttribute('data-bgset', imageUrl)
  placeholder.style.backgroundImage = `url('${imageUrl}')`
}

/**
 * @param {HTMLImageElement} image
 * @param {string} imageUrl
 * @param {boolean} isPriority
 */
const setImageNodeSource = (image, imageUrl, isPriority) => {
  image.src = imageUrl
  image.loading = isPriority ? 'eager' : 'lazy'
  image.decoding = 'async'
  image.setAttribute('fetchpriority', isPriority ? 'high' : 'auto')
}

/**
 * @param {HTMLElement} slide
 * @param {string} imageUrl
 * @param {number} index
 */
const ensureMainSlideImageUrl = (slide, imageUrl, index) => {
  const renderableImageUrl = getRenderableImageUrl(imageUrl)
  const label = `Variant image ${index + 1}`
  slide.setAttribute('aria-label', label)

  const link = findOneElement(slide, '.js-lightbox-trigger')
  if (link instanceof HTMLAnchorElement) {
    link.href = imageUrl
  }

  const placeholder = findOneElementOfType(HTMLElement)(
    slide,
    `.${IMAGE_PLACEHOLDER_CLASS}`
  )
  if (placeholder) {
    setImagePlaceholder(placeholder, renderableImageUrl, label)
    return
  }

  const pictureSources = findElements(slide, 'picture source')
  pictureSources.forEach((source) => {
    source.setAttribute('srcset', '')
  })

  const image = findOneElementOfType(HTMLImageElement)(slide, 'img')
  if (image) {
    image.srcset = ''
    image.sizes = ''
    setImageNodeSource(image, renderableImageUrl, index === 0)
  }
}

/**
 * @param {HTMLElement} slide
 * @param {string} imageUrl
 * @param {number} index
 */
const ensureThumbSlideImageUrl = (slide, imageUrl, index) => {
  const renderableImageUrl = getRenderableImageUrl(imageUrl)
  const label = `Variant thumbnail ${index + 1}`
  const placeholder = findOneElementOfType(HTMLElement)(
    slide,
    `.${THUMB_PLACEHOLDER_CLASS}`
  )

  if (placeholder) {
    setImagePlaceholder(placeholder, renderableImageUrl, label)
    return
  }

  const image = findOneElementOfType(HTMLImageElement)(slide, 'img')
  if (image) {
    image.alt = label
    setImageNodeSource(image, renderableImageUrl, index === 0)
  }
}

/**
 * @param {HTMLElement[]} items
 */
const showGlobalSlides = (items) => {
  items.forEach((item) => {
    if (item.getAttribute('data-item-alt-handle') === 'all') {
      showSlide(item)
    }
  })
}

/**
 * @param {HTMLElement} slide
 * @param {string} imageUrl
 * @returns {boolean}
 */
const slideAlreadyShowsUrl = (slide, imageUrl) => {
  const lightboxTrigger = findOneElementOfType(HTMLAnchorElement)(
    slide,
    '.js-lightbox-trigger'
  )
  const currentUrl = lightboxTrigger?.href
    ? normalizeImageUrl(lightboxTrigger.href)
    : null
  const targetUrl = normalizeImageUrl(imageUrl)

  return Boolean(
    currentUrl &&
      targetUrl &&
      currentUrl === targetUrl &&
      slide.querySelector('img')
  )
}

/**
 * @param {{ mainItems: HTMLElement[], thumbItems: HTMLElement[], mainImageMediaIndexes: number[] }} galleryIndex
 * @param {string[]} variantImageUrls
 */
const hydrateVariantSlidesIntoGallery = (galleryIndex, variantImageUrls) => {
  variantImageUrls.forEach((imageUrl, index) => {
    const mediaIndex = galleryIndex.mainImageMediaIndexes[index]

    if (typeof mediaIndex !== 'number') {
      return
    }

    const mainSlide = galleryIndex.mainItems[mediaIndex]

    if (
      !mainSlide ||
      !mainSlide.classList.contains('js-product-gallery-image')
    ) {
      return
    }

    const thumbSlide = galleryIndex.thumbItems[mediaIndex] ?? null

    if (!thumbSlide) {
      return
    }

    if (!slideAlreadyShowsUrl(mainSlide, imageUrl)) {
      ensureMainSlideImageUrl(mainSlide, imageUrl, index)
      ensureThumbSlideImageUrl(thumbSlide, imageUrl, index)
    }

    showSlide(mainSlide)
    showSlide(thumbSlide)
  })
}

/**
 * Re-filter the gallery for a variant's images. Returns whether variant mode is
 * active (i.e. at least one variant image was applied).
 *
 * @param {{ galleryIndex: object, variantImageUrls?: string[] | null }} input
 * @returns {boolean}
 */
const updateGallerySlidesForVariantImages = ({
  galleryIndex,
  variantImageUrls
}) => {
  const uniqueVariantImageUrls = []
  const seenNormalizedUrls = new Set()

  ;(variantImageUrls ?? []).forEach((imageUrl) => {
    const normalizedUrl = normalizeImageUrl(imageUrl)
    if (!normalizedUrl || seenNormalizedUrls.has(normalizedUrl)) {
      return
    }

    seenNormalizedUrls.add(normalizedUrl)
    uniqueVariantImageUrls.push(imageUrl)
  })

  if (uniqueVariantImageUrls.length === 0) {
    galleryIndex.mainItems.forEach((item) => {
      if (item.getAttribute('data-item-alt-handle') === 'variant') {
        hideSlide(item)
        return
      }

      showSlide(item)
    })

    galleryIndex.thumbItems.forEach((item) => {
      if (item.getAttribute('data-item-alt-handle') === 'variant') {
        hideSlide(item)
        return
      }

      showSlide(item)
    })

    return false
  }

  preloadVariantImages(uniqueVariantImageUrls)

  // Variant mode: hide all original gallery items.
  galleryIndex.mainItems.forEach((item) => {
    hideSlide(item)
  })

  galleryIndex.thumbItems.forEach((item) => {
    hideSlide(item)
  })

  hydrateVariantSlidesIntoGallery(galleryIndex, uniqueVariantImageUrls)

  // Keep product-level media (for example the accentuate featured video)
  // visible across variant-specific gallery filtering.
  showGlobalSlides(galleryIndex.mainItems)
  showGlobalSlides(galleryIndex.thumbItems)

  return true
}

/**
 * Read the selected variant and its images from the PDP product form's
 * hydration data.
 *
 * @param {Element | null} carousel
 * @returns {{ selectedVariantId: number | null, variantImageUrlsByVariantId: Map<number, string[]> }}
 */
const getInitialVariantData = (carousel) => {
  const emptyResult = {
    selectedVariantId: null,
    variantImageUrlsByVariantId: new Map()
  }

  const productId = carousel?.getAttribute('data-product-id')
  if (!productId) {
    return emptyResult
  }

  const productIdAsNumber = Number(productId)
  if (!Number.isFinite(productIdAsNumber)) {
    return emptyResult
  }

  const dataScripts = Array.from(
    document.querySelectorAll('product-form[data-layout="pdp"] .js-data')
  )

  for (const script of dataScripts) {
    try {
      const appData = JSON.parse(script.textContent || '{}')

      if (
        typeof appData.hydrate_data !== 'string' ||
        appData.hydrate_data.length === 0
      ) {
        continue
      }

      const hydratedProduct = JSON.parse(appData.hydrate_data)

      if (hydratedProduct.product?.id !== productIdAsNumber) {
        continue
      }

      const selectedVariantId =
        appData.variant_id ??
        hydratedProduct.metafields?.selected_or_first_available_variant?.id ??
        null

      const variantImageUrlsByVariantId = new Map()
      ;(hydratedProduct.variants ?? []).forEach((item) => {
        const variantId = item?.variant?.id
        if (typeof variantId !== 'number') {
          return
        }

        const variantUrls = (item?.metafields?.variant_images ?? []).filter(
          (url) => typeof url === 'string'
        )

        if (variantUrls.length === 0) {
          return
        }

        variantImageUrlsByVariantId.set(variantId, Array.from(new Set(variantUrls)))
      })

      if (variantImageUrlsByVariantId.size === 0) {
        ;(hydratedProduct.metafields?.options_with_values ?? [])
          .flatMap((option) => option.values ?? [])
          .forEach((value) => {
            const variantId = value?.variant?.id
            if (typeof variantId !== 'number') {
              return
            }

            const variantUrls = (value?.swatch?.variant_images ?? []).filter(
              (url) => typeof url === 'string'
            )

            if (variantUrls.length === 0) {
              return
            }

            const currentVariantUrls =
              variantImageUrlsByVariantId.get(variantId) ?? []
            variantImageUrlsByVariantId.set(
              variantId,
              Array.from(new Set([...currentVariantUrls, ...variantUrls]))
            )
          })
      }

      return { selectedVariantId, variantImageUrlsByVariantId }
    } catch {
      continue
    }
  }

  return emptyResult
}

/**
 * Warm the cache and preload the first few variant images.
 *
 * @param {string[]} imageUrls
 */
const preloadVariantImages = (imageUrls) => {
  const preloadTargets = imageUrls.slice(0, MAX_VARIANT_PRELOADS)

  imageUrls.forEach((imageUrl) => {
    void warmImageCache(imageUrl)
  })

  preloadTargets.forEach((imageUrl, index) => {
    const existingPreload = findOneElementOfType(HTMLLinkElement)(
      document.head,
      `link[${VARIANT_PRELOAD_ATTRIBUTE}="${index}"]`
    )

    if (existingPreload) {
      existingPreload.href = imageUrl
      existingPreload.setAttribute(
        'fetchpriority',
        index === 0 ? 'high' : 'auto'
      )
    } else {
      const preloadLink = document.createElement('link')
      preloadLink.rel = 'preload'
      preloadLink.as = 'image'
      preloadLink.href = imageUrl
      preloadLink.setAttribute(VARIANT_PRELOAD_ATTRIBUTE, String(index))
      preloadLink.setAttribute('fetchpriority', index === 0 ? 'high' : 'auto')
      document.head.appendChild(preloadLink)
    }

    const image = new Image()
    image.decoding = 'async'
    image.src = getRenderableImageUrl(imageUrl)
  })
}

/**
 * Fullscreen image viewer. Replaces the former PhotoSwipe 4 lightbox: the
 * native `<dialog>` provides Esc-to-close, focus handling and top-layer
 * stacking, so only the trigger wiring is left. The trigger's `href` (a single
 * 2048px image URL) becomes the viewer's background image.
 *
 * @param {HTMLElement} root
 * @param {object} namespace
 */
const handleLightbox = (root, namespace) => {
  const dialog = findOneElement(root, '.js-product-gallery-lightbox')

  if (!(dialog instanceof HTMLDialogElement)) {
    return
  }

  const image = findOneElement(dialog, '.js-product-gallery-lightbox-image')

  namespace.addDelegatedEventListener(
    root,
    '.js-lightbox-trigger',
    'click',
    (target, evt) => {
      evt.preventDefault()

      const imageUrl = target.getAttribute('href') || ''

      if (!imageUrl || image == null) {
        return
      }

      image.style.backgroundImage = `url('${imageUrl}')`

      if (!dialog.open) {
        dialog.showModal()
      }
    }
  )

  // The dialog is its own backdrop: a click that lands on the dialog rather
  // than on a child came from outside the image.
  namespace.addDirectEventListener(dialog, 'click', (_element, evt) => {
    if (evt.target === dialog) {
      dialog.close()
    }
  })

  namespace.addDelegatedEventListener(
    dialog,
    '.js-product-gallery-lightbox-close',
    'click',
    () => {
      dialog.close()
    }
  )
}

class ProductImages extends HTMLElement {
  #cleanup = null

  connectedCallback() {
    if (this.#cleanup != null) {
      return
    }

    const mainBus = initMainBus()
    const namespace = makeEventNamespace()

    const carousel = findOneElement(this, '.js-product-gallery-images')
    const mainTrack = findOneElement(this, '[data-gallery-track]')
    const thumbTrack = findOneElement(this, '[data-thumbs-track]')
    const prevButton = findOneElement(this, '.js-product-gallery-prev')
    const nextButton = findOneElement(this, '.js-product-gallery-next')
    const pagination = findOneElement(this, '.js-product-gallery-pagination')
    const videoContainers = findElements(this, '.js-product-gallery-video')
    const featuredVideoMediaNodes = findElements(
      this,
      '.js-product-gallery-featuredVideoMedia'
    )

    if (carousel == null || mainTrack == null || thumbTrack == null) {
      namespace.destroy()
      return
    }

    let galleryIndex = null
    const initialVariantData = getInitialVariantData(carousel)

    galleryIndex = createGalleryIndex(mainTrack, thumbTrack)

    const initialVariantImageUrls =
      initialVariantData.selectedVariantId != null
        ? initialVariantData.variantImageUrlsByVariantId.get(
            initialVariantData.selectedVariantId
          ) ?? null
        : null

    updateGallerySlidesForVariantImages({
      galleryIndex,
      variantImageUrls: initialVariantImageUrls
    })

    // ---- Carousel mechanics (scroll-snap, no Swiper) ----

    let activeIndex = 0
    let rafId = null
    let paginationButtons = []

    const visibleMainSlides = () =>
      getGalleryItems(mainTrack).filter(
        (item) => !item.classList.contains('u-hide')
      )

    const visibleThumbSlides = () =>
      getGalleryItems(thumbTrack).filter(
        (item) => !item.classList.contains('u-hide')
      )

    const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth')

    /**
     * @param {HTMLElement} track
     * @param {HTMLElement[]} slides
     * @param {number} index
     * @param {ScrollBehavior} behavior
     */
    const scrollTrackToIndex = (track, slides, index, behavior) => {
      const slide = slides[index]
      if (slide == null || typeof track.scrollTo !== 'function') {
        return
      }

      const trackRect = track.getBoundingClientRect()
      const slideRect = slide.getBoundingClientRect()

      track.scrollTo({
        left: track.scrollLeft + (slideRect.left - trackRect.left),
        behavior
      })
    }

    /**
     * @param {HTMLElement} track
     * @param {HTMLElement[]} slides
     * @returns {number}
     */
    const indexFromScroll = (track, slides) => {
      if (slides.length === 0) {
        return 0
      }

      let bestIndex = 0
      let bestDistance = Number.POSITIVE_INFINITY

      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - track.scrollLeft)
        if (distance < bestDistance) {
          bestDistance = distance
          bestIndex = index
        }
      })

      return bestIndex
    }

    // ---- Video controls ----

    const videoControls = videoContainers
      .map((wrapper) => {
        const slide = wrapper.closest(GALLERY_ITEM_SELECTOR)
        const video = findOneElement(wrapper, 'video')
        const playButton = findOneElement(wrapper, '.js-product-gallery-videoPlay')
        const muteButton = findOneElement(wrapper, '.js-product-gallery-videoMute')

        if (
          !slide ||
          !(video instanceof HTMLVideoElement) ||
          !(playButton instanceof HTMLButtonElement) ||
          !(muteButton instanceof HTMLButtonElement)
        ) {
          return null
        }

        return { slide, video, playButton, muteButton }
      })
      .filter(isPresent)

    /**
     * @param {{ video: HTMLVideoElement, playButton: HTMLButtonElement, muteButton: HTMLButtonElement }} control
     */
    const updateButtonLabels = ({ video, playButton, muteButton }) => {
      const isPaused = video.paused
      const isMuted = video.muted

      playButton.setAttribute('data-state', isPaused ? 'play' : 'pause')
      playButton.setAttribute(
        'aria-label',
        isPaused ? 'Play video' : 'Pause video'
      )

      muteButton.setAttribute('data-state', isMuted ? 'unmute' : 'mute')
      muteButton.setAttribute(
        'aria-label',
        isMuted ? 'Unmute video' : 'Mute video'
      )
    }

    const syncVideosWithActiveSlide = () => {
      const slides = visibleMainSlides()
      const activeSlide = slides[activeIndex] ?? null

      videoControls.forEach((control) => {
        const isActive = control.slide === activeSlide

        if (isActive) {
          control.video.play().catch(() => {})
        } else {
          control.video.pause()
        }

        updateButtonLabels(control)
      })
    }

    videoControls.forEach((control) => {
      namespace.addDirectEventListener(control.playButton, 'click', () => {
        if (control.video.paused) {
          control.video.play().catch(() => {})
        } else {
          control.video.pause()
        }

        updateButtonLabels(control)
      })

      namespace.addDirectEventListener(control.muteButton, 'click', () => {
        control.video.muted = !control.video.muted
        updateButtonLabels(control)
      })
    })

    const buildPagination = () => {
      if (pagination == null) {
        return
      }

      pagination.replaceChildren()
      paginationButtons = []

      const slides = visibleMainSlides()

      if (slides.length <= 1) {
        return
      }

      slides.forEach((_slide, index) => {
        const button = document.createElement('button')
        button.type = 'button'
        button.className = 'product-gallery__pagination-bullet'
        button.setAttribute('aria-label', `Go to image ${index + 1}`)
        namespace.addDirectEventListener(button, 'click', () => {
          scrollTrackToIndex(
            mainTrack,
            visibleMainSlides(),
            index,
            scrollBehavior()
          )
        })
        pagination.append(button)
        paginationButtons.push(button)
      })
    }

    const updateTabIndexes = () => {
      const slides = visibleMainSlides()

      slides.forEach((slide, index) => {
        const isActive = index === activeIndex

        findElements(slide, 'a, button, [tabindex]').forEach((el) => {
          el.setAttribute('tabindex', isActive ? '0' : '-1')
          el.setAttribute('aria-hidden', isActive ? 'false' : 'true')
        })
      })
    }

    const syncActiveState = () => {
      const slides = visibleMainSlides()
      const thumbs = visibleThumbSlides()
      const index = indexFromScroll(mainTrack, slides)

      if (index === activeIndex) {
        return
      }

      activeIndex = index

      slides.forEach((slide, slideIndex) => {
        slide.setAttribute(
          'data-active',
          slideIndex === index ? 'true' : 'false'
        )
      })

      thumbs.forEach((thumb, thumbIndex) => {
        thumb.setAttribute(
          'data-active',
          thumbIndex === index ? 'true' : 'false'
        )
      })

      paginationButtons.forEach((button, buttonIndex) => {
        button.classList.toggle(
          'product-gallery__pagination-bullet--active',
          buttonIndex === index
        )
      })

      scrollTrackToIndex(thumbTrack, thumbs, index, 'auto')
      updateTabIndexes()
      syncVideosWithActiveSlide()
    }

    const onMainScroll = () => {
      if (rafId != null) {
        return
      }

      rafId = window.requestAnimationFrame(() => {
        rafId = null
        syncActiveState()
      })
    }

    /**
     * @param {1 | -1} direction
     */
    const scrollByMainSlide = (direction) => {
      const slides = visibleMainSlides()
      if (slides.length === 0) {
        return
      }

      const next = Math.min(
        Math.max(indexFromScroll(mainTrack, slides) + direction, 0),
        slides.length - 1
      )

      scrollTrackToIndex(mainTrack, slides, next, scrollBehavior())
    }

    namespace.addDirectEventListener(mainTrack, 'scroll', onMainScroll)

    if (prevButton) {
      namespace.addDirectEventListener(prevButton, 'click', () => {
        scrollByMainSlide(-1)
      })
    }

    if (nextButton) {
      namespace.addDirectEventListener(nextButton, 'click', () => {
        scrollByMainSlide(1)
      })
    }

    namespace.addDelegatedEventListener(
      thumbTrack,
      GALLERY_ITEM_SELECTOR,
      'click',
      (thumb) => {
        const thumbs = visibleThumbSlides()
        const index = thumbs.indexOf(thumb)

        if (index >= 0) {
          scrollTrackToIndex(
            mainTrack,
            visibleMainSlides(),
            index,
            scrollBehavior()
          )
        }
      }
    )

    // ---- Featured video ----

    featuredVideoMediaNodes.forEach((featuredVideoMedia) => {
      setFeaturedVideoState(featuredVideoMedia, 'paused')

      namespace.addDirectEventListener(featuredVideoMedia, 'click', () => {
        const isPlaying =
          featuredVideoMedia.getAttribute('data-state') === 'playing'

        const iframe = findOneElementOfType(HTMLIFrameElement)(
          featuredVideoMedia,
          'iframe'
        )

        if (iframe) {
          const originalSrc =
            iframe.getAttribute('data-original-src') ||
            iframe.getAttribute('src') ||
            ''

          if (!originalSrc) {
            return
          }

          iframe.setAttribute('data-original-src', originalSrc)

          if (!isPlaying) {
            dismissFeaturedVideoPoster(featuredVideoMedia)
            iframe.setAttribute('src', withVideoAutoplay(originalSrc))
            setFeaturedVideoState(featuredVideoMedia, 'playing')
          }

          return
        }

        const video = findOneElementOfType(HTMLVideoElement)(
          featuredVideoMedia,
          'video'
        )

        if (!video) {
          return
        }

        if (isPlaying) {
          video.pause()
          setFeaturedVideoState(featuredVideoMedia, 'paused')
        } else {
          dismissFeaturedVideoPoster(featuredVideoMedia)
          void video.play().catch(() => {})
          setFeaturedVideoState(featuredVideoMedia, 'playing')
        }
      })

      const video = findOneElementOfType(HTMLVideoElement)(
        featuredVideoMedia,
        'video'
      )

      if (video) {
        namespace.addDirectEventListener(video, 'ended', () => {
          setFeaturedVideoState(featuredVideoMedia, 'paused')
        })
      }
    })

    handleLightbox(this, namespace)

    // ---- Variant changes ----

    const mainBusHandlerRemover = mainBus
      .on('notification:selected-variant')
      .do(({ details: { product, selectedVariant, variantImageUrls } }) => {
        galleryIndex = createGalleryIndex(mainTrack, thumbTrack)

        const selectedVariantImageUrls =
          (variantImageUrls && variantImageUrls.length > 0
            ? variantImageUrls
            : null) ??
          (selectedVariant?.id != null
            ? initialVariantData.variantImageUrlsByVariantId.get(
                selectedVariant.id
              ) ?? null
            : null)

        updateGallerySlidesForVariantImages({
          galleryIndex,
          variantImageUrls: selectedVariantImageUrls
        })

        buildPagination()
        activeIndex = -1
        mainTrack.scrollTo({ left: 0, behavior: 'auto' })
        thumbTrack.scrollTo({ left: 0, behavior: 'auto' })
        syncActiveState()

        const badgesElement = carousel.querySelector(
          `[data-productGallery-badges="${product.id}"]`
        )

        if (badgesElement && selectedVariant) {
          const outOfStockBadge = findOneElement(
            badgesElement,
            '[data-badge-type="out-of-stock"]'
          )
          const productBadges = findElements(
            badgesElement,
            '[data-badge-type="product-badge"]'
          )

          if (outOfStockBadge) {
            outOfStockBadge.style.display = selectedVariant.available
              ? 'none'
              : 'block'
          }

          productBadges.forEach((badge) => {
            badge.style.display = selectedVariant.available ? 'block' : 'none'
          })
        }
      })

    buildPagination()
    activeIndex = -1
    syncActiveState()

    this.#cleanup = () => {
      mainBusHandlerRemover()
      namespace.destroy()

      if (rafId != null) {
        window.cancelAnimationFrame(rafId)
        rafId = null
      }

      imageObjectUrlCache.forEach((objectUrl) => {
        URL.revokeObjectURL(objectUrl)
      })
      imageObjectUrlCache.clear()
      imageWarmPromiseCache.clear()
    }
  }

  disconnectedCallback() {
    this.#cleanup?.()
    this.#cleanup = null
  }
}

// Sections can be re-rendered by the theme editor; defining twice throws.
if (!customElements.get('product-images')) {
  customElements.define('product-images', ProductImages)
}
